import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import NewspaperRoundedIcon from '@mui/icons-material/NewspaperRounded';
import { PRACTICE_TOPIC_VIEWS, buildPracticeTopicPath, buildTopicViewLink } from '../../utils/contentRoutes';
import { resolveTopicIcon } from '../../utils/topicIcons';
import { PRACTICE_LEVELS, getSectionIcon } from './practiceConfig';

/**
 * The practice topics, merged across the dialogue and discussion tracks by their
 * shared id (id 1 = Health in dialogue, discussion AND vocab). Grouping by id is
 * grouping by name, so one entry per topic carries what each topic offers:
 *
 *   hasDialogue    — the topic has dialogue scenarios (ids 1–11)
 *   hasDiscussion  — the topic has discussion prompts  (ids 1–16)
 *   vocab/flashcards are available for every topic here (all have a vocab set).
 *
 * Static on purpose: the map and sidebar are public (no auth), and topic ids /
 * names / emoji are stable content. Mirrors french-fide-be dialogue_topics.json
 * and discussion_topics.json.
 */
export const TOPIC_CATALOG = [
  { id: 1, emoji: '🏥', name: { fr: 'Santé', de: 'Gesundheit', en: 'Health' }, hasDialogue: true, hasDiscussion: true },
  { id: 2, emoji: '🧰', name: { fr: 'Travail', de: 'Arbeit', en: 'Work' }, hasDialogue: true, hasDiscussion: true },
  { id: 3, emoji: '🏠', name: { fr: 'Logement', de: 'Wohnen', en: 'Housing' }, hasDialogue: true, hasDiscussion: true },
  { id: 4, emoji: '👶', name: { fr: 'Enfants', de: 'Kinder', en: 'Children' }, hasDialogue: true, hasDiscussion: true },
  {
    id: 5,
    emoji: '🛒',
    name: { fr: 'Achats', de: 'Einkaufen', en: 'Shopping' },
    hasDialogue: true,
    hasDiscussion: true,
  },
  {
    id: 6,
    emoji: '🏦',
    name: { fr: 'Poste, banque et assurances', de: 'Post, Bank und Versicherungen', en: 'Post, Bank and Insurance' },
    hasDialogue: true,
    hasDiscussion: true,
  },
  {
    id: 7,
    emoji: '🚆',
    name: { fr: 'Mobilité', de: 'Mobilität', en: 'Mobility' },
    hasDialogue: true,
    hasDiscussion: true,
  },
  {
    id: 8,
    emoji: '📺',
    name: { fr: 'Médias et loisirs', de: 'Medien und Freizeit', en: 'Media and Leisure' },
    hasDialogue: true,
    hasDiscussion: true,
  },
  {
    id: 9,
    emoji: '🎓',
    name: { fr: 'Cours de formation', de: 'Ausbildung und Kurse', en: 'Education and Training' },
    hasDialogue: true,
    hasDiscussion: true,
  },
  {
    id: 10,
    emoji: '💼',
    name: { fr: 'Recherche d’emploi', de: 'Jobsuche', en: 'Job Search' },
    hasDialogue: true,
    hasDiscussion: true,
  },
  {
    id: 11,
    emoji: '🧾',
    name: { fr: 'Administration', de: 'Verwaltung', en: 'Administration' },
    hasDialogue: true,
    hasDiscussion: true,
  },
  { id: 12, emoji: '🍽️', name: { fr: 'Nourriture', de: 'Essen', en: 'Food' }, hasDialogue: false, hasDiscussion: true },
  { id: 13, emoji: '✈️', name: { fr: 'Voyage', de: 'Reise', en: 'Travel' }, hasDialogue: false, hasDiscussion: true },
  { id: 14, emoji: '🎉', name: { fr: 'Fête', de: 'Feier', en: 'Party' }, hasDialogue: false, hasDiscussion: true },
  {
    id: 15,
    emoji: '👕',
    name: { fr: 'Vêtements', de: 'Kleidung', en: 'Clothes' },
    hasDialogue: false,
    hasDiscussion: true,
  },
  { id: 16, emoji: '🌦️', name: { fr: 'Météo', de: 'Wetter', en: 'Weather' }, hasDialogue: false, hasDiscussion: true },
];

/**
 * The A2 guided sections' own word sets (main-vocab topics 17–19: Introduction,
 * Situation description, Telephone). They are real vocabulary/flashcard topics but
 * have no dialogue or discussion track, so they only join the catalog in those two
 * materials — see `topicsForMaterial` in practiceMaterials.js. `sectionSlug` is what
 * marks the level-rail destination; the topic catalog itself uses `/fide-practice/topics/…`.
 */
export const SECTION_VOCAB_TOPIC_LIST = (PRACTICE_LEVELS.find((level) => level.id === 'a2')?.sections ?? [])
  .filter((section) => section.vocabTopicId)
  .map((section) => ({
    id: section.vocabTopicId,
    sectionSlug: section.slug,
    name: section.navName,
    Icon: getSectionIcon(section),
    hasDialogue: false,
    hasDiscussion: false,
  }));

/**
 * Derived from the list above rather than hardcoded, so it follows SECTION_VOCAB_TOPIC.
 * These are also the paid word sets — main-vocab.json marks the same three `free: false`,
 * and a backend test holds the two in step.
 */
export const A2_SECTION_TOPIC_IDS = new Set(SECTION_VOCAB_TOPIC_LIST.map((topic) => topic.id));

/** True for the word sets that come from the A2 exam tasks — the ones the "A2 Topic" badge marks. */
export const isA2SectionTopic = (topicId) => A2_SECTION_TOPIC_IDS.has(Number(topicId));

/**
 * The three B1 discussion topics authored as free account-preview samples —
 * pinned to match french-fide-be's `discussionTopicsFreeAccess.test.ts`.
 *
 * The API can't tell a guest which discussion topics these are (it reports
 * every one of them as `hidden` to an unauthenticated request on purpose — see
 * useLockedTopicIds), so this is display-only: it drives the "Free" badge a
 * guest sees, never an access decision. A guest still needs an account to open
 * any of them; only after signing in does the API's own `hidden` flag confirm
 * it for real.
 */
export const B1_FREE_SAMPLE_TOPIC_IDS = new Set([1, 2, 3]);

/** Every topic here has its own vocab set (and therefore flashcards). */
export const topicHasVocab = () => true;

export const getTopicName = (topic, lang = 'fr') =>
  topic.name[lang] ?? topic.name.fr ?? topic.name.en ?? `Topic ${topic.id}`;

// resolveTopicIcon keyword-matches, and a couple of ids collide (its 'med' rule
// catches "Medien"), so pin those; the rest resolve fine from the names.
const ICON_OVERRIDE = { 6: AccountBalanceRoundedIcon, 8: NewspaperRoundedIcon };

/** The blue topic icon shown on the map/catalog for a topic. */
export const getTopicIcon = (topic) =>
  topic.Icon || ICON_OVERRIDE[topic.id] || resolveTopicIcon(`${topic.name.fr} ${topic.name.de}`);

export const getTopicById = (topicId) => TOPIC_CATALOG.find((topic) => topic.id === Number(topicId)) ?? null;

/**
 * Where a topic card leads.
 *
 * `preferredView` is the view the learner is already on (or was on last time), so
 * moving between topics keeps the tab they were reading instead of resetting to
 * dialogue. It is honoured for any of the four views, but a caller that only knows
 * a *remembered* view should pass dialogue/discussion only — landing on flashcards
 * because that is where you left off is not what picking a theme asks for.
 *
 * With nothing preferred (or a view this topic lacks) it falls back to the first
 * available tab: dialogue, else the discussion prompts, else vocabulary — always
 * somewhere real.
 */
export const getTopicEntryPath = (lang, topic, preferredView = null) => {
  if (PRACTICE_TOPIC_VIEWS.includes(preferredView)) {
    return buildPracticeTopicPath(lang, topic.id, resolveTopicView(topic.id, preferredView));
  }
  if (topic.hasDialogue) return buildPracticeTopicPath(lang, topic.id, 'dialogue');
  if (topic.hasDiscussion) return buildPracticeTopicPath(lang, topic.id, 'discussion');
  return buildPracticeTopicPath(lang, topic.id, 'vocab');
};

/**
 * Clamp a requested view to one the topic actually has. Only dialogue can be
 * missing (ids 12–16 are discussion-only); vocab/flashcards exist for every topic.
 */
export const resolveTopicView = (topicId, view) => {
  const topic = getTopicById(topicId);
  if (!topic) return view;
  if (view === 'dialogue' && !topic.hasDialogue) return topic.hasDiscussion ? 'discussion' : 'vocab';
  if (view === 'discussion' && !topic.hasDiscussion) return topic.hasDialogue ? 'dialogue' : 'vocab';
  return view;
};

/**
 * The Dialogue/Discussion halves of a catalog topic's tab bar (the page supplies
 * Vocab + Flashcards). Each track's tab is disabled where the topic has no
 * content. Returns `null` for anything that isn't a catalog topic (e.g. the A2
 * practice sections 17–19), so those keep their existing three-tab bar.
 *
 * `fromLevel` marks arrival through B1 → Discussion (the Level rail). Dialogue
 * drops out of the bar there, and every link keeps the level/track URL shape so
 * moving between tabs stays on the level rail. From the Topic catalog the links
 * use the clean topic shape instead — the URL shape is what distinguishes the two.
 */
export const buildTopicTabProps = (lang, topicId, { fromLevel = false } = {}) => {
  const topic = getTopicById(topicId);
  if (!topic) return null;

  if (fromLevel) {
    return {
      discussionTo: buildTopicViewLink(lang, topic.id, 'discussion', { fromLevel: true }),
      discussionEnabled: topic.hasDiscussion,
    };
  }

  return {
    dialogueTo: buildPracticeTopicPath(lang, topic.id, 'dialogue'),
    discussionTo: buildPracticeTopicPath(lang, topic.id, 'discussion'),
    dialogueEnabled: topic.hasDialogue,
    discussionEnabled: topic.hasDiscussion,
  };
};
